import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ApiError } from "@am/shared";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { Lang } from "../i18n/strings";

export function LoginPage() {
  const { login, sessionExpiredFlash, consumeExpiredFlash } = useAuth();
  const { t, tHtml, lang, setLang } = useI18n();

  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert "Plain & <em>Emphasised</em>" into JSX. The login h1 uses an <em>
  // segment for the second word (Settings / Paramètres) in the legacy markup.
  const h1Plain = t("login_h1_plain");
  const emTarget = t("login_h1_em_target");
  const h1Before = emTarget && h1Plain.endsWith(emTarget)
    ? h1Plain.slice(0, h1Plain.length - emTarget.length)
    : h1Plain;
  const h1Em = emTarget && h1Plain.endsWith(emTarget) ? emTarget : "";

  useEffect(() => {
    if (sessionExpiredFlash) {
      setError(t("session_expired"));
      consumeExpiredFlash();
    }
    // We only want to react once, when the flash is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExpiredFlash]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(slug, password, remember);
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 401
        ? t("login_error")
        : err instanceof Error
          ? err.message
          : t("error_generic");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login" id="login-view">
      <div className="lbg" aria-hidden="true">
        <div className="lbg-aura" />
        <div className="lbg-slab-l" />
        <div className="lbg-slab-r" />
        <div className="lbg-rays" />
        <div className="lbg-horizon" />
        <div className="lbg-vignette" />
      </div>
      <div className="login-card">
        <div className="login-brand">
          <div className="login-mark">
            <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
              <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 110 2h-1v1a3 3 0 01-3 3H7a3 3 0 01-3-3v-1H3a1 1 0 110-2h1a7 7 0 017-7h1V5.73A2 2 0 0112 2zM9.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm5 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
            </svg>
          </div>
          <div>
            <div className="login-brand-name">
              <span className="brand-ams">AM</span> AI Assistant
            </div>
            <div className="login-brand-sub">{t("login_brand_sub")}</div>
          </div>
        </div>

        <h1>
          {h1Before}
          {h1Em && <em>{h1Em}</em>}
        </h1>
        <p className="subtitle">{t("login_subtitle")}</p>

        {error && (
          <div className="login-error" style={{ display: "block" }}>
            {error}
          </div>
        )}

        {!forgotOpen && (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label>{t("lbl_slug")}</label>
              <input
                type="text"
                autoComplete="username"
                placeholder="mon-entreprise"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>{t("lbl_password")}</label>
              <div className="password-wrap">
                <input
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pwd-toggle"
                  aria-label={showPwd ? t("pwd_hide") : t("pwd_show")}
                  onClick={() => setShowPwd((v) => !v)}
                >
                  {showPwd ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="remember-row">
              <input
                type="checkbox"
                id="f-remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <label htmlFor="f-remember">{t("lbl_remember")}</label>
            </div>
            <button type="submit" className="btn-primary" disabled={submitting} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {submitting ? t("saving") : <>{t("login_btn")} <ArrowRight size={15} /></>}
            </button>
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <button
                type="button"
                className="forgot-link"
                onClick={() => setForgotOpen(true)}
              >
                {t("forgot_link")}
              </button>
            </div>
          </form>
        )}

        {forgotOpen && (
          <div className="forgot-panel" style={{ display: "block" }}>
            <div className="forgot-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>
            <p className="forgot-title">{t("forgot_title")}</p>
            <p
              className="forgot-desc"
              dangerouslySetInnerHTML={{ __html: tHtml("forgot_desc") }}
            />
            <button type="button" className="btn-back" onClick={() => setForgotOpen(false)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <ArrowLeft size={14} /> {t("forgot_back")}
            </button>
          </div>
        )}

        <div style={{ marginTop: 20, textAlign: "center" }}>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
            style={{
              appearance: "none",
              WebkitAppearance: "none",
              background: "transparent",
              backgroundImage:
                "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27none%27%3E%3Cpath stroke=%27%236a6359%27 stroke-width=%271.5%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 d=%27M4 6l4 4 4-4%27/%3E%3C/svg%3E')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 4px center",
              backgroundSize: "12px",
              border: "none",
              color: "var(--text-muted)",
              fontSize: 12,
              cursor: "pointer",
              paddingRight: 20,
            }}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
          </select>
        </div>

        <div className="login-footer">
          Powered by{" "}
          <a href="https://amsolutions.tech" target="_blank" rel="noopener noreferrer">
            AM Solutions
          </a>
        </div>
      </div>
    </div>
  );
}
